import {initDebugger} from "../støttefunksjoner/debug";
import {EntitetReferanseSpesifikasjon, KlientId, OppdaterSkjemalåsSpesifikasjon, OppdaterSkjemalåsStatus, OpprettSkjemalåsSpesifikasjon, OpprettSkjemalåsStatus, SkjemalåsModell, SkjemalåsRessursClient, SkjemalåsSkjemaId} from "./ressurser/generertNg2HttpKlient";
import {BehaviorSubject, concat, exhaustMap, filter, interval, Observable, Subject, switchMap, takeUntil, timer} from "rxjs";
import {erNil} from "../støttefunksjoner/nilsjekk";
import {map, tap} from "rxjs/operators";
import {verifiserErNil} from "../støttefunksjoner/antakelser";
import {erLikeObjekterDyp} from "felles-ng2/støttefunksjoner/objekter";

const debug = initDebugger('Skjemalåshåndtering');

/**
 * Klasse for håndtering av skjemalås/gjenopprettelse av ulagret skjema.
 *
 * Virkemåte:
 * Ved initiering av redigering forespør klient en lås på dette skjema.
 * På server lagres da en lås med info om hvilket skjema, initial-tilstanden til dette samt hvilken bruker som forespurte låsen.
 * Ved redigering av skjema sendes periodisk en serialisert representasjon av skjemaet til server.
 *
 * Dersom en lås allerede eksisterer for det aktuelle skjemaet håndteres følgende tilstander:
 * - {@link ErrorResponsStatus#låstAvAnnenBruker}: annen bruker redigerer sammen skjema.
 *     Annen brukers skjemalås blir gjort tilgjengelig via {@link SkjemalåsErrorRespons#konflikt}.
 * - {@link ErrorResponsStatus#låstAvSammeBrukerAnnetVindu}: kallende bruker har allerede opprettet en skjemalås på samme skjema, men
 *     lagrede data har blitt endret siden denne ble opprettet.
 *     Eksisterende skjemalås blir gjort tilgjengelig via {@link SkjemalåsErrorRespons#konflikt}.
 * - {@link OkResponsStatus#ingenEndringerLåsOvertatt}: lagrede data har ikke blitt endret siden eksisterende skjemalås ble opprettet (dette
 *     avgjøres på serversiden ved å sammenlikne initialtilstand lagret i skjemalås med innsendt skjematilstand),
 *     alternativt har lagrede data blitt oppdatert men {@link #opprettSkjemalåsForsinket} bla kalt med `overtaEksisterendeLås`=true.
 *     I begge tilfeller forutsettes det eksisterende lås har blitt opprettet av kallende bruker.
 *     Normalt bør kaller oppdatere lokalt skjema med skjema mottatt i {@link SkjemalåsModell#skjema}.
 *
 * Skjemalåsen vil periodisk bli oppdatert med serialisert skjemadatarepresentasjon slik at skjemaet
 * kan gjenopprettes dersom klienten skulle krasje eller bli lukket ved et uhell, dette håndteres via
 * tilstanden {@link OkResponsStatus#ingenEndringerLåsOvertatt}.
 *
 * @typeParam T - Typen til skjemaspesifikasjonen
 */
export class Skjemalåshåndtering<T> {

    constructor(
      /** Metode som returnerer skjemaspesifikasjonen som serialiserbart objekt */
      private readonly skjemaspesifikasjon: () => T,
      /** Id som identifiserer skjematypen som låsen gjelder for */
      private readonly skjemaId: SkjemalåsSkjemaId,
      /** Kallende klients unike id (f.eks. {@link Appkonfigurasjon#klientId})*/
      private readonly klientId: KlientId,
      private readonly skjemalåsRessurs: SkjemalåsRessursClient,
      /** Antall millisekunder mellom hvert forsøk på å arkivere skjema-data */
      private readonly oppdaterSkjemalåsIntervall = 15 * 1000) {
    }

    /** Skjemalås opprettet for kallende bruker */
    private skjemalås: SkjemalåsModell | null = null;
    /** Observerbar som lever verdi hver gang skjemalås-opprettelse/oppdatering-prosessen starter og slutter (når det skjer ressurskall ifbm opprettelse og oppdatering) */
    private _skjemalåsoppdateringPågår = new BehaviorSubject(false);
    /** Subject som terminerer opprett/oppdater-lås prosessene */
    private skjemalåsFrigitt = new Subject<void>();

    /** Observerbar som lever verdi hver gang skjemalås-opprettelse/oppdatering-prosessen starter og slutter (når det skjer ressurskall ifbm opprettelse og oppdatering) */
    get skjemalåsoppdateringPågår(): Observable<boolean> {
        return this._skjemalåsoppdateringPågår;
    }

    /**
     * Oppretter skjemalås.
     * Dersom 'overtaEksisterendeLaas' = true vil skjemalås overtas av kallende bruker
     * selvom lagrede data har blitt endret siden låsen ble opprettet.
     * Dette skjer kun dersom eksisterende lås eies av kallende bruker
     */
    opprettSkjemalåsForsinket(eiendeGruppeId: string, rotentitetId: EntitetReferanseSpesifikasjon, overtaEksisterendeLås: boolean, forsinkelse: number): Observable<SkjemalåsOkRespons> {
        debug('opprettSkjemalåsForsinket')
        verifiserErNil(this.skjemalås, 'opprettSkjemalåsForsinket kalt uten at tidligere abonnemang har blitt avsluttet gjennom kall til frigiSkjemalås()');

        return concat(
          //opprett skjemalås etter initiell forsinkelse
          timer(forsinkelse).pipe(
            switchMap(() => this.opprettSkjemalås(eiendeGruppeId, rotentitetId, overtaEksisterendeLås))),
          //oppdater skjemalås periodisk
          interval(this.oppdaterSkjemalåsIntervall).pipe(
            map(() => this.hentSkjemaspesifikasjon(true)),
            //kun oppdater lås dersom skjema har blitt endret siden forrige oppdatering
            filter(skjemaverdier => this.erSkjemaEndret(skjemaverdier)),
            //kjør requester serielt - vent til forrige er ferdig før ny fyres
            exhaustMap(skjemaverdier => this.oppdaterSkjemalås(skjemaverdier)) //@todo hva ved feil i denne?
          )
        ).pipe(
          //produser forvent responsmodell, kast exception ved ikke-ok respons
          map(respons => this.håndterRespons(respons)),
          //stopp oppdatering av skjemalås når denne frigis
          takeUntil(this.skjemalåsFrigitt)
        )
    }

    /** Frigir aktiv skjemalås og stopper periodisk oppdatering av denne */
    async frigiSkjemalås() {
        //Sørg for å terminere eventuelt aktivt skjemalås-abonnement,
        //dette vil også føre til at eventuelle pågående ressurskall blir avbrutt (Angular's HttpClient sørger for dette)
        //Dersom det finnes en pågående lås-oppdater-request (server har ikke svart enda), kan det oppstå en race condition mellom
        //lås-frigi kallet og oppdateringskallet på server-siden?
        this.skjemalåsFrigitt.next();

        if (erNil(this.skjemalås)) return; //ingen skjemalås har blitt opprettet enda, ergo ingen ting å frigi

        //frigi lås
        try {
            await this.skjemalåsRessurs.frigi({id: this.skjemalås.id, klientId: this.klientId}).somPromise();
        } catch (e) {
            //@todo håndter feil
        }
        this.skjemalås = null;
    }

    /** Terminerer asynkrone prosesser initiert av skjemalåshåndtering.
     * Merk at vi her ikke frigir eventuell opprettet skjemalås, til det formålet må {@link #frigiSkjemalås} kalles */
    terminer() {
        this.skjemalåsFrigitt.next();
        this.skjemalås = null;
    }

    //hjelpemetoder

    private erSkjemaEndret(skjemaverdier: T) {
        if (erLikeObjekterDyp(skjemaverdier, this.skjemalås?.skjema as T)) {
            debug('periodisk oppdatering: ingen endringer i skjema, avbryter')
            return false;
        }
        return true;
    }

    /** Maserer angitt {@link SkjemalåsRespons} til en {@link SkjemalåsOkRespons} dersom lås ble opprettet/oppdatert eller tatt over,
     * kaster exception av typen {@link SkjemalåsErrorRespons} dersom låst av annen bruker eller av samme bruker i annet vindu. */
    private håndterRespons(respons: SkjemalåsRespons): SkjemalåsOkRespons {
        switch (respons.status) {
            case OpprettSkjemalåsStatus.ingenEndringerLåsOvertatt:
                return {status: OkResponsStatus.ingenEndringerLåsOvertatt, laas: respons.laas} as SkjemalåsOkRespons
            case OpprettSkjemalåsStatus.låsOpprettet:
                return {status: OkResponsStatus.låsOpprettet, laas: respons.laas} as SkjemalåsOkRespons
            case OppdaterSkjemalåsStatus.låsOppdatert:
                return {status: OkResponsStatus.låsOppdatert, laas: respons.laas} as SkjemalåsOkRespons

            case OpprettSkjemalåsStatus.låstAvAnnenBruker:
            case OppdaterSkjemalåsStatus.låstAvAnnenBruker:
                throw new SkjemalåsErrorRespons(ErrorResponsStatus.låstAvAnnenBruker, respons.konflikt!);
            case OpprettSkjemalåsStatus.låstAvSammeBrukerAnnetVindu:
            case OppdaterSkjemalåsStatus.låstAvSammeBrukerAnnetVindu:
                throw new SkjemalåsErrorRespons(ErrorResponsStatus.låstAvSammeBrukerAnnetVindu, respons.konflikt!)

            default:
                throw new Error(`ukjent enum ${respons.status}`)
        }
    }

    private opprettSkjemalås(eiendeGruppeId: string, rotentitetId: EntitetReferanseSpesifikasjon, overtaEksisterendeLås: boolean): Observable<SkjemalåsRespons> {
        this._skjemalåsoppdateringPågår.next(true);
        let spesifikasjon = this.opprettelseSpesifikasjon(eiendeGruppeId, rotentitetId, overtaEksisterendeLås);
        debug("opprettSkjemalås", spesifikasjon)
        let opprettSkjemalåsResponsModellRequestRespons = this.skjemalåsRessurs.opprett(spesifikasjon);
        let opprettSkjemalåsResponsModellObservable = opprettSkjemalåsResponsModellRequestRespons.somObservable();
        return opprettSkjemalåsResponsModellObservable
          .pipe(
            map(respons => ({status: respons.status, laas: respons.ervervetLaas, konflikt: respons.laasIKonflikt} as SkjemalåsRespons)),
            tap({next: respons => this.skjemalås = respons.laas, finalize: () => this._skjemalåsoppdateringPågår.next(false)}));
    }

    private opprettelseSpesifikasjon(eiendeGruppeId: string, rotentitetId: EntitetReferanseSpesifikasjon, overtaEksisterendeLås: boolean): OpprettSkjemalåsSpesifikasjon {
        return {
            eiendeGruppeId: eiendeGruppeId,
            rotentitetId: rotentitetId,
            skjemaId: this.skjemaId,
            skjema: this.hentSkjemaspesifikasjon() as Object,
            klientId: this.klientId,
            overtaEksisterendeLaas: overtaEksisterendeLås
        }
    }

    /** Henter skjemaspesifikasjonen.
     * Kaster exception ved feil med mindre ignorerException er satt til true, da returneres skjema lagret i sist mottatte skjemalås,
     * som på denne måte sørger for at skjemalås-oppdateringsprosessen ikke stopper */
    private hentSkjemaspesifikasjon(ignorerException = false): T {
        try {
            return this.skjemaspesifikasjon();
        } catch (e) {
            console.warn("Feil ved henting av skjemaspesifikasjon", e);
            if (ignorerException) return (this.skjemalås?.skjema ?? {}) as T;
            else throw e;
        }
    }

    private oppdaterSkjemalås(skjemaverdier: T): Observable<SkjemalåsRespons> {
        this._skjemalåsoppdateringPågår.next(true);
        let spesifikasjon = {id: this.skjemalås!.id, skjema: skjemaverdier, klientId: this.klientId} as OppdaterSkjemalåsSpesifikasjon;
        debug("oppdaterSkjemalås", spesifikasjon)
        return this.skjemalåsRessurs.oppdater(spesifikasjon).somObservable()
          .pipe(
            map(respons => ({status: respons.status, laas: respons.oppdatertLaas, konflikt: respons.laasIKonflikt} as SkjemalåsRespons)),
            tap({next: respons => this.skjemalås = respons.laas, finalize: () => this._skjemalåsoppdateringPågår.next(false)}));
    }
}

export interface SkjemalåsRespons {
    status: OpprettSkjemalåsStatus | OppdaterSkjemalåsStatus;
    /** Oppdatert skjemalås, tom dersom status er låstAvSammeBrukerAnnetVindu eller låstAvAnnenBruker */
    laas: SkjemalåsModell | null;
    /** Eksisterende lås som er i konflikt, tom dersom ingen konflikt eksisterer (status = låsOppdatert) */
    konflikt: SkjemalåsModell | null;
}

export enum OkResponsStatus {
    /** Ingen lås eksisterte ved opprettelse, ny lås opprettet */
    låsOpprettet,
    /** En lås eksisterte ved opprettelse,
     * men dennes initial-tilstand ({@link SkjemalåsModell#initieltSkjema}) var lik skjema sendt i opprettelses-requesten ({@link OpprettSkjemalåsSpesifikasjon#skjema}),
     * eller det ble i opprettelses-requesten angitt av lås skulle overtas ({@link OpprettSkjemalåsSpesifikasjon#ignorerDataendringer}) selvom lagrede data ikke er lik initial-tilstand
     * låsen ble derfor overtatt av kaller, i dette siste tilfellet bør klienten oppdatere skjemaet med data mottatt i {@link SkjemalåsModell#skjema}.
     * Merk at den eksisterende låsen i alle tilfeller var opprette av kallende bruker. */
    ingenEndringerLåsOvertatt,
    /** Lås ble oppdatert, dvs {@link SkjemalåsModell#skjema} har blitt oppdatert ihht spesifikasjon og {@link SkjemalåsModell#oppdatertDato} satt til 'nå' */
    låsOppdatert
}


export interface SkjemalåsOkRespons {
    status: OkResponsStatus;
    laas: SkjemalåsModell;
}

export enum ErrorResponsStatus {låstAvAnnenBruker, låstAvSammeBrukerAnnetVindu}

export class SkjemalåsErrorRespons {
    readonly status: ErrorResponsStatus;
    readonly konflikt: SkjemalåsModell;

    constructor(status: ErrorResponsStatus, konflikt: SkjemalåsModell) {
        this.status = status;
        this.konflikt = konflikt;
    }
}
